import * as i0 from '@angular/core';
import { Component, Input, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import * as i2 from '@mhe/ngx-shared';
import { ButtonPurpose, ButtonModule } from '@mhe/ngx-shared';
import * as i1 from '@angular/cdk/a11y';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i4 from '@mhe/ngx-shared/tooltip';
import { TooltipModule } from '@mhe/ngx-shared/tooltip';
import { MatDialogModule } from '@angular/material/dialog';

var Direction;
(function (Direction) {
    Direction["Down"] = "down";
    Direction["Left"] = "left";
    Direction["Right"] = "right";
    Direction["Up"] = "up";
})(Direction || (Direction = {}));
var ButtonId;
(function (ButtonId) {
    ButtonId["ZoomInButton"] = "zoom-in-button";
    ButtonId["ZoomOutButton"] = "zoom-out-button";
    ButtonId["ResetImageButton"] = "reset-image-button";
    ButtonId["MoveUpButton"] = "move-up-button";
    ButtonId["MoveDownButton"] = "move-down-button";
    ButtonId["MoveLeftButton"] = "move-left-button";
    ButtonId["MoveRightButton"] = "move-right-button";
})(ButtonId || (ButtonId = {}));
var Lang;
(function (Lang) {
    Lang["EnUs"] = "en-US";
    Lang["EsEs"] = "es-ES";
})(Lang || (Lang = {}));

const enUs = {
    close: 'Close',
    move_down: 'Move down',
    move_left: 'Move left',
    move_right: 'Move right',
    move_up: 'Move up',
    image_moved: {
        down: 'Image moved down',
        left: 'Image moved left',
        right: 'Image moved right',
        up: 'Image moved up',
    },
    reset_image: 'Reset image',
    zoom_in_image: 'Zoom in image',
    zoom_out_image: 'Zoom out image',
};

const esEs = {
    close: 'Cerrar',
    move_down: 'Mover hacia abajo',
    move_left: 'Mover a la izquierda',
    move_right: 'Mover a la derecha',
    move_up: 'Mover hacia arriba',
    image_moved: {
        down: 'Imagen movida hacia abajo',
        left: 'Imagen movida a la izquierda',
        right: 'Imagen movida a la derecha',
        up: 'Imagen movida hacia arriba',
    },
    reset_image: 'Reiniciar imagen',
    zoom_in_image: 'Acercar imagen',
    zoom_out_image: 'Alejar imagen',
};

class LargeImageNavigatorComponent {
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
        this.ButtonPurpose = ButtonPurpose;
        this.Direction = Direction;
        this.defaultScale = {
            current: 1,
            min: 1,
            max: 3,
            step: 0.2,
        };
        this.defaultLength = {
            current: 0,
            min: -0.4,
            max: 0.4,
            step: 0.1,
        };
    }
    ngOnInit() {
        this.setTranslations();
        this.resetImage();
    }
    setTranslations() {
        const langMap = new Map([
            [Lang.EnUs, enUs],
            [Lang.EsEs, esEs],
        ]);
        this.translations = langMap.get(this.lang) || enUs;
    }
    resetImage() {
        this.scale = { ...this.defaultScale };
        this.xLength = { ...this.defaultLength };
        this.yLength = { ...this.defaultLength };
        this.updateTransformStyle();
        this.enable(ButtonId.ZoomInButton);
        this.focus(ButtonId.ZoomInButton);
        this.disable(ButtonId.ZoomOutButton);
        this.disable(ButtonId.ResetImageButton);
        this.showMoveButtons = false;
    }
    focus(id) {
        document.getElementById(id)?.focus();
    }
    zoomIn() {
        if (!this.canZoomIn()) {
            this.focus(ButtonId.ZoomOutButton);
            this.disable(ButtonId.ZoomInButton);
        }
        this.scale.current = this.roundToTenths(this.scale.current + this.scale.step);
        this.updateTransformStyle();
        this.announceScaleChange();
        this.enable(ButtonId.ZoomOutButton);
        this.enable(ButtonId.ResetImageButton);
        this.showMoveButtons = true;
    }
    disable(id) {
        document.getElementById(id)?.setAttribute('disabled', '');
    }
    enable(id) {
        document.getElementById(id)?.removeAttribute('disabled');
    }
    canZoomIn() {
        return this.scale.current + this.scale.step < this.scale.max;
    }
    zoomOut() {
        if (!this.canZoomOut()) {
            this.focus(ButtonId.ZoomInButton);
            this.disable(ButtonId.ZoomOutButton);
            this.disable(ButtonId.ResetImageButton);
            this.showMoveButtons = false;
            queueMicrotask(() => {
                this.resetImage();
            });
        }
        this.scale.current = this.roundToTenths(this.scale.current - this.scale.step);
        this.updateTransformStyle();
        this.announceScaleChange();
        this.enable(ButtonId.ZoomInButton);
    }
    canZoomOut() {
        return this.scale.current - this.scale.step > this.scale.min;
    }
    canMoveUp() {
        return this.yLength.current + this.yLength.step < this.yLength.max;
    }
    moveUp() {
        if (!this.canMoveUp()) {
            this.focus(ButtonId.MoveDownButton);
            this.disable(ButtonId.MoveUpButton);
        }
        this.yLength.current = this.roundToTenths(this.yLength.current + this.yLength.step);
        this.updateTransformStyle();
        this.announceImageMovement(Direction.Up);
        this.enable(ButtonId.MoveDownButton);
    }
    canMoveDown() {
        return this.yLength.current - this.yLength.step > this.yLength.min;
    }
    moveDown() {
        if (!this.canMoveDown()) {
            this.focus(ButtonId.MoveUpButton);
            this.disable(ButtonId.MoveDownButton);
        }
        this.yLength.current = this.roundToTenths(this.yLength.current - this.yLength.step);
        this.updateTransformStyle();
        this.announceImageMovement(Direction.Down);
        this.enable(ButtonId.MoveUpButton);
    }
    canMoveLeft() {
        return (this.roundToTenths(this.xLength.current + this.xLength.step) <
            this.xLength.max);
    }
    moveLeft() {
        if (!this.canMoveLeft()) {
            this.focus(ButtonId.MoveRightButton);
            this.disable(ButtonId.MoveLeftButton);
        }
        this.xLength.current = this.roundToTenths(this.xLength.current + this.xLength.step);
        this.updateTransformStyle();
        this.announceImageMovement(Direction.Left);
        this.enable(ButtonId.MoveRightButton);
    }
    canMoveRight() {
        return (this.roundToTenths(this.xLength.current - this.xLength.step) >
            this.xLength.min);
    }
    moveRight() {
        if (!this.canMoveRight()) {
            this.focus(ButtonId.MoveLeftButton);
            this.disable(ButtonId.MoveRightButton);
        }
        this.xLength.current = this.roundToTenths(this.xLength.current - this.xLength.step);
        this.updateTransformStyle();
        this.announceImageMovement(Direction.Right);
        this.enable(ButtonId.MoveLeftButton);
    }
    updateTransformStyle() {
        this.transformStyle = this.transformStyle = `
			scale(${this.scale.current})
			translateX(${this.xLength.current * 100}%)
			translateY(${this.yLength.current * 100}%)
      `;
    }
    roundToTenths(num) {
        return Math.round(num * 10) / 10;
    }
    announceScaleChange() {
        this.liveAnnouncer.announce(`${Math.round(this.scale.current * 100)}%`);
        this.liveAnnouncer.clear();
    }
    announceImageMovement(direction) {
        this.liveAnnouncer.announce(this.translations.image_moved[direction]);
        this.liveAnnouncer.clear();
    }
}
LargeImageNavigatorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LargeImageNavigatorComponent, deps: [{ token: i1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component });
LargeImageNavigatorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: LargeImageNavigatorComponent, selector: "mhe-large-image-navigator", inputs: { src: "src", alt: "alt", credit: "credit", lang: "lang" }, ngImport: i0, template: "<div class=\"lin-container\">\n  <div class=\"image-mask\">\n    <img class=\"image\" data-testid=\"image\" [src]=\"src\" [alt]=\"alt\" draggable=\"false\"\n      [style.transform]=\"transformStyle\" />\n  </div>\n\n  <div class=\"credit\" *ngIf=\"credit\">\n    <small class=\"credit-content\" data-testid=\"credit\">{{ credit }}</small>\n  </div>\n  <footer>\n    <div class=\"action-group\">\n      <button id=\"zoom-in-button\" mhe-button class=\"action-item action-button\" data-testid=\"zoom-in-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.zoom_in_image\"\n        [mheTooltip]=\"translations.zoom_in_image\" (click)=\"zoomIn()\">\n        <i class=\"ahe-icon dpg-icon-magnifying-glass-plus-symbol\"></i>\n      </button>\n      <div class=\"action-item zoom-level\" data-testid=\"zoom-level\">\n        {{ scale.current | percent }}\n      </div>\n      <button id=\"zoom-out-button\" disabled mhe-button class=\"action-item action-button\" data-testid=\"zoom-out-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.zoom_out_image\"\n        [mheTooltip]=\"translations.zoom_out_image\" (click)=\"zoomOut()\">\n        <i class=\"ahe-icon dpg-icon-magnifying-glass-minus-symbol\"></i>\n      </button>\n      <button id=\"reset-image-button\" disabled mhe-button class=\"action-item action-button\"\n        data-testid=\"reset-image-button\" [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.reset_image\"\n        [mheTooltip]=\"translations.reset_image\" (click)=\"resetImage(); this.announceScaleChange()\">\n        <i class=\"ahe-icon dpg-icon-refresh\"></i>\n      </button>\n    </div>\n    <div class=\"action-group\" *ngIf=\"showMoveButtons\">\n      <button id=\"move-up-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-up-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_up\" [mheTooltip]=\"translations.move_up\"\n        (click)=\"moveUp()\">\n        <i class=\"ahe-icon dpg-icon-up-arrow\"></i>\n      </button>\n      <button id=\"move-down-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-down-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_down\" [mheTooltip]=\"translations.move_down\"\n        (click)=\"moveDown()\">\n        <i class=\"ahe-icon dpg-icon-down-arrow\"></i>\n      </button>\n      <button id=\"move-left-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-left-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_left\" [mheTooltip]=\"translations.move_left\"\n        (click)=\"moveLeft()\">\n        <i class=\"ahe-icon dpg-icon-left-arrow\"></i>\n      </button>\n      <button id=\"move-right-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-right-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_right\"\n        [mheTooltip]=\"translations.move_right\" (click)=\"moveRight()\">\n        <i class=\"ahe-icon dpg-icon-right-arrow\"></i>\n      </button>\n    </div>\n  </footer>\n</div>\n", styles: [":host{height:inherit;width:inherit}.lin-container{display:flex;flex-direction:column;height:100%;width:100%}.image-mask{flex:1 1 auto;overflow:hidden;padding:10px}.image{object-fit:contain;height:100%;width:100%;display:block;margin:0 auto;transition:.5s ease-out}.credit{padding:0 0 .5rem}.credit-content{color:#46707d;background-color:#fff;min-height:30px;padding:3px 20px;font-size:14px;font-weight:400}footer{flex:0 0 auto;flex-wrap:wrap;width:100%;display:flex;padding:8px 10px;background-color:#f0f3f4;border-top:2px solid #d1dbde;color:#174d5c}.action-group{display:flex;align-items:center}@media (min-width: 576px){.action-item{margin:0 5px}}.action-button{display:flex;align-items:center}.action-button:disabled{border:1px solid transparent}.zoom-level{min-width:50px;text-align:center}\n"], components: [{ type: i2.MheButtonComponent, selector: "button[mhe-button]", inputs: ["ariaExpanded", "type", "class", "enableLight", "purpose", "checked"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.TooltipDirective, selector: "[mheTooltip]", inputs: ["mheTooltip", "placement", "disableDefaultActions", "disableTooltip", "panelClass", "alternateAriaLabel"], outputs: ["shown", "hidden", "toggleShown"], exportAs: ["mheTooltip"] }], pipes: { "percent": i3.PercentPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LargeImageNavigatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'mhe-large-image-navigator', template: "<div class=\"lin-container\">\n  <div class=\"image-mask\">\n    <img class=\"image\" data-testid=\"image\" [src]=\"src\" [alt]=\"alt\" draggable=\"false\"\n      [style.transform]=\"transformStyle\" />\n  </div>\n\n  <div class=\"credit\" *ngIf=\"credit\">\n    <small class=\"credit-content\" data-testid=\"credit\">{{ credit }}</small>\n  </div>\n  <footer>\n    <div class=\"action-group\">\n      <button id=\"zoom-in-button\" mhe-button class=\"action-item action-button\" data-testid=\"zoom-in-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.zoom_in_image\"\n        [mheTooltip]=\"translations.zoom_in_image\" (click)=\"zoomIn()\">\n        <i class=\"ahe-icon dpg-icon-magnifying-glass-plus-symbol\"></i>\n      </button>\n      <div class=\"action-item zoom-level\" data-testid=\"zoom-level\">\n        {{ scale.current | percent }}\n      </div>\n      <button id=\"zoom-out-button\" disabled mhe-button class=\"action-item action-button\" data-testid=\"zoom-out-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.zoom_out_image\"\n        [mheTooltip]=\"translations.zoom_out_image\" (click)=\"zoomOut()\">\n        <i class=\"ahe-icon dpg-icon-magnifying-glass-minus-symbol\"></i>\n      </button>\n      <button id=\"reset-image-button\" disabled mhe-button class=\"action-item action-button\"\n        data-testid=\"reset-image-button\" [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.reset_image\"\n        [mheTooltip]=\"translations.reset_image\" (click)=\"resetImage(); this.announceScaleChange()\">\n        <i class=\"ahe-icon dpg-icon-refresh\"></i>\n      </button>\n    </div>\n    <div class=\"action-group\" *ngIf=\"showMoveButtons\">\n      <button id=\"move-up-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-up-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_up\" [mheTooltip]=\"translations.move_up\"\n        (click)=\"moveUp()\">\n        <i class=\"ahe-icon dpg-icon-up-arrow\"></i>\n      </button>\n      <button id=\"move-down-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-down-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_down\" [mheTooltip]=\"translations.move_down\"\n        (click)=\"moveDown()\">\n        <i class=\"ahe-icon dpg-icon-down-arrow\"></i>\n      </button>\n      <button id=\"move-left-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-left-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_left\" [mheTooltip]=\"translations.move_left\"\n        (click)=\"moveLeft()\">\n        <i class=\"ahe-icon dpg-icon-left-arrow\"></i>\n      </button>\n      <button id=\"move-right-button\" mhe-button class=\"action-item action-button\" data-testid=\"move-right-button\"\n        [purpose]=\"ButtonPurpose.Icon\" [attr.aria-label]=\"translations.move_right\"\n        [mheTooltip]=\"translations.move_right\" (click)=\"moveRight()\">\n        <i class=\"ahe-icon dpg-icon-right-arrow\"></i>\n      </button>\n    </div>\n  </footer>\n</div>\n", styles: [":host{height:inherit;width:inherit}.lin-container{display:flex;flex-direction:column;height:100%;width:100%}.image-mask{flex:1 1 auto;overflow:hidden;padding:10px}.image{object-fit:contain;height:100%;width:100%;display:block;margin:0 auto;transition:.5s ease-out}.credit{padding:0 0 .5rem}.credit-content{color:#46707d;background-color:#fff;min-height:30px;padding:3px 20px;font-size:14px;font-weight:400}footer{flex:0 0 auto;flex-wrap:wrap;width:100%;display:flex;padding:8px 10px;background-color:#f0f3f4;border-top:2px solid #d1dbde;color:#174d5c}.action-group{display:flex;align-items:center}@media (min-width: 576px){.action-item{margin:0 5px}}.action-button{display:flex;align-items:center}.action-button:disabled{border:1px solid transparent}.zoom-level{min-width:50px;text-align:center}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.LiveAnnouncer }]; }, propDecorators: { src: [{
                type: Input
            }], alt: [{
                type: Input
            }], credit: [{
                type: Input
            }], lang: [{
                type: Input
            }] } });

class LargeImageNavigatorModule {
}
LargeImageNavigatorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LargeImageNavigatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LargeImageNavigatorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LargeImageNavigatorModule, declarations: [LargeImageNavigatorComponent], imports: [CommonModule, MatDialogModule, ButtonModule, TooltipModule], exports: [LargeImageNavigatorComponent] });
LargeImageNavigatorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LargeImageNavigatorModule, imports: [[CommonModule, MatDialogModule, ButtonModule, TooltipModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: LargeImageNavigatorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MatDialogModule, ButtonModule, TooltipModule],
                    declarations: [LargeImageNavigatorComponent],
                    exports: [LargeImageNavigatorComponent],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

/*
 * Public API Surface of ngx-large-image-navigator
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LargeImageNavigatorComponent, LargeImageNavigatorModule };
